/**
 * ShareEmailForm.tsx
 * 
 * This component handles the email input and sharing functionality for posits.
 * It includes form validation, loading states, and error handling for the email
 * sharing process.
 * 
 * Changes:
 * - Extracted from ShareDialog.tsx to improve component modularity
 * - Added analytics tracking for share events
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from "sonner";
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { Loader2 } from 'lucide-react';

interface ShareEmailFormProps {
  positId: string;
  shareUrl: string;
  onShare: () => void;
  isPremium?: boolean;
}

export function ShareEmailForm({ positId, shareUrl, onShare, isPremium = true }: ShareEmailFormProps) {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Parse comma-separated emails
  const parseEmails = (emailString: string): string[] => {
    return emailString
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);
  };

  // Validate single email
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleShare = async () => {
    if (!user) {
      toast.error("You must be logged in to share");
      return;
    }

    if (!email) {
      toast.error("Please enter email address(es)");
      return;
    }

    // Parse and validate emails
    const emails = parseEmails(email);
    const invalidEmails: string[] = [];
    const validEmails: string[] = [];
    
    emails.forEach(emailAddr => {
      if (!isValidEmail(emailAddr)) {
        invalidEmails.push(emailAddr);
      } else if (emailAddr.toLowerCase() === user.email?.toLowerCase()) {
        invalidEmails.push(emailAddr + " (cannot share with yourself)");
      } else {
        validEmails.push(emailAddr.toLowerCase());
      }
    });

    // Remove duplicates from valid emails
    const uniqueValidEmails = [...new Set(validEmails)];

    if (invalidEmails.length > 0) {
      toast.error(`Invalid emails: ${invalidEmails.join(', ')}`);
      if (uniqueValidEmails.length === 0) return;
    }

    if (uniqueValidEmails.length === 0) {
      toast.error("No valid email addresses to share with");
      return;
    }

    setIsLoading(true);
    const results = {
      successful: [] as string[],
      alreadyShared: [] as string[],
      failed: [] as string[]
    };

    try {
      // Track share attempt
      trackEvent(ANALYTICS_EVENTS.SHARE_POSIT, {
        share_type: 'email',
        is_premium: isPremium,
        email_count: uniqueValidEmails.length
      });

      // Update posit privacy to exclusive first
      const { error: privacyError } = await supabase
        .from("posits")
        .update({ privacy: "exclusive" })
        .eq("id", positId);

      if (privacyError) {
        throw new Error('Failed to update posit privacy settings. Please try again.');
      }

      // Process each email
      for (const emailAddr of uniqueValidEmails) {
        try {
          // Check if share already exists
          const { data: existingShare, error: checkError } = await supabase
            .from("posit_shares")
            .select("id")
            .eq("posit_id", positId)
            .eq("user_email", emailAddr)
            .maybeSingle();

          if (checkError) {
            results.failed.push(emailAddr);
            continue;
          }

          if (existingShare) {
            results.alreadyShared.push(emailAddr);
            continue;
          }

          // Create share
          const { error: shareError } = await supabase
            .from("posit_shares")
            .insert({
              posit_id: positId,
              user_email: emailAddr,
              created_by: user.id,
            });

          if (shareError) {
            results.failed.push(emailAddr);
          } else {
            results.successful.push(emailAddr);
          }
        } catch (error) {
          results.failed.push(emailAddr);
        }
      }

      // Show detailed results
      if (results.successful.length > 0) {
        trackEvent(ANALYTICS_EVENTS.SHARE_POSIT, {
          status: 'success',
          share_type: 'email',
          is_premium: isPremium,
          successful_count: results.successful.length
        });
        
        if (results.successful.length === 1) {
          toast.success(`Shared with ${results.successful[0]}`);
        } else {
          toast.success(`Successfully shared with ${results.successful.length} recipients`);
        }
      }

      if (results.alreadyShared.length > 0) {
        toast.warning(`Already shared with: ${results.alreadyShared.join(', ')}`);
      }

      if (results.failed.length > 0) {
        toast.error(`Failed to share with: ${results.failed.join(', ')}`);
      }

      // Clear input if any emails were processed successfully
      if (results.successful.length > 0 || results.alreadyShared.length > 0) {
        setEmail("");
        onShare(); // Refresh the list of shared users
      }

    } catch (error: any) {
      trackEvent(ANALYTICS_EVENTS.SHARE_POSIT, {
        status: 'error',
        error: error.message,
        share_type: 'email',
        is_premium: isPremium
      });
      toast.error(error.message || "Failed to share posit");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex gap-2 mt-4">
      <Input
        placeholder="Enter email address(es), separated by commas"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="text-sm"
      />
      <Button 
        onClick={handleShare} 
        disabled={isLoading}
        className="shrink-0"
      >
        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Share"}
      </Button>
    </div>
  );
} 